import { appUI } from 'lib/app-ui';
import { zoomUI } from 'lib/zoom-ui';
import { pui } from 'lib/perspectives-ui';
import { Node, Link } from '../store';
import { regionUI } from 'lib/region-ui';
import { drawingUI } from 'lib/drawing-ui';
import { COLOR_VAR_TO_HEX_MAPPING } from 'lib/constants';
import LZString from 'lz-string';

class ExportScreenshots {
  constructor() {
    this.setupAsyncFunctions();
    this.isPPTexport = false;
    this.exportedElements = null;
  }

  setExportedElements(elements) {
    this.exportedElements = elements;
  }

  getExportedElements() {
    return this.exportedElements;
  }

  getExportedRegions() {
    return regionUI.getSelectedRegions();
  }

  getExportedDrawings() {
    return drawingUI.getSelectedDrawings();
  }

  getExportedNodes() {
    if (!this.exportedElements) {
      return [];
    }
    return this.exportedElements.filter(e => e.instance && e.instance instanceof Node);
  }

  getExportedLinks() {
    if (!this.exportedElements) {
      return [];
    }
    return this.exportedElements.filter(e => e.instance && e.instance instanceof Link);
  }

  exportSelection() {
    if (this.isPPTexport) return true;
    if (!this.exportedElements || !this.exportedElements.length) {
      return false;
    }
    return true;
  }

  setupAsyncFunctions() {
    this.getCredentials = async () => {
      try {
        const credentials = await $.ajax({
          type: 'GET',
          url: `/api/maps/${this.canvas.store.access.publicIdentifier}/download/token`,
        });
        return credentials;
      } catch (e) {
        return;
      }
    };

    this.beginImageDownload = async ({imageHeightField, imageWidthField,imageHTMLField,imageTokenField,imageForm}) => {
      this.isPPTexport = false;
      this.canvas.mapMenuTag.hide();
      const size = exportScreenshots.getMapSize();
      // if map is greater than max size, scale it down to fit in that space
      this._updateSize({size});

      const credentials = await this.getCredentials();
      if (!credentials || !credentials.token) {
        alert('There was an error downloading this map.');
        return;
      }

      let html = exportScreenshots._getMapHTML({
        body: exportScreenshots._prepareHtml({size}),
        styleSheetText: exportScreenshots._cssText(),
        subscriber: credentials.subscriber,
      });

      imageHeightField.value = size.height + 200;
      imageWidthField.value = size.width + 200 + size.sidebarWidth;
      imageHTMLField.value = html;
      imageTokenField.value = credentials.token;

      this.canvas.showTempDownloadStatus();
      imageForm.submit();
    };

    this.beginPDFDownload = async ({pdfHeightField, pdfWidthField, pdfOrientationField, pdfHTMLField, pdfTokenField, pdfForm}) => {
      this.canvas.mapMenuTag.hide();
      this.isPPTexport = false;
      const size = exportScreenshots.getMapSize();
      // if map is greater than max size, scale it down to fit in that space
      this._updateSize({size});

      let orientation = 'landscape';
      if (size.height > size.width) {
        orientation = 'portrait';
      }

      const credentials = await this.getCredentials();
      if (!credentials || !credentials.token) {
        alert('There was an error downloading this map.');
        return;
      }
      let html = exportScreenshots._getMapHTML({
        body: exportScreenshots._prepareHtml({size}),
        styleSheetText: exportScreenshots._cssText(),
        orientationCss: orientation == 'landscape' ? exportScreenshots._getLandscapePrintCss() : '',
        subscriber: credentials.subscriber
      });

      pdfHeightField.value = size.height + 200;
      pdfWidthField.value = size.width + 200 + size.sidebarWidth;
      pdfOrientationField.value = orientation;
      pdfHTMLField.value = html;
      pdfTokenField.value = credentials.token;

      this.canvas.showTempDownloadStatus();
      pdfForm.submit();
    };

    this.beginPPTDownload = async ({pptHeightField, pptWidthField, pptSlidesField, pptHTMLField, pptTokenField, pptPrintEntireMapField, pptBackgroundField, pptForm}) => {
      this.canvas.mapMenuTag.hide();

      const credentials = await this.getCredentials();
      if (!credentials || !credentials.token) {
        alert('There was an error downloading this map.');
        return;
      }

      const slides = [];

      let hasWayPoints = this.canvas.store.presentations[0]
          && this.canvas.store.presentations[0].waypoints.length > 0;

      if (!hasWayPoints) {
        this.isPPTexport = false;
        const size = exportScreenshots.getMapSize();
      // if map is greater than max size, scale it down to fit in that space
        this._updateSize({size});

        let orientation = 'landscape';
        if (size.height > size.width) {
          orientation = 'portrait';
        }

        const credentials = await this.getCredentials();
        if (!credentials || !credentials.token) {
          alert('There was an error downloading this map.');
          return;
        }

        let html = exportScreenshots._getMapHTML({
          body: exportScreenshots._prepareHtml({size}),
          styleSheetText: exportScreenshots._cssText(),
          orientationCss: orientation == 'landscape' ? exportScreenshots._getLandscapePrintCss() : '',
          subscriber: credentials.subscriber
        });

        pptHeightField.value = size.height + 200;
        pptWidthField.value = size.width + 200 + size.sidebarWidth;
        pptHTMLField.value = html;
        pptTokenField.value = credentials.token;
        pptBackgroundField.value = COLOR_VAR_TO_HEX_MAPPING[window.user.settings.background_color];
        pptPrintEntireMapField.value = true;

        this.canvas.showTempDownloadStatus();
        pptForm.submit();
        return;
      }

      this.isPPTexport = true;
      this.canvas.showTempDownloadStatus();

      const unprocessedSlides = this.canvas.store.presentations[0]
            && this.canvas.store.presentations[0].waypoints.length > 0
            && this.canvas.store.presentations[0].waypoints
            || [{
              nodeIds: this.canvas.store.getNodesArray().map(n => n.id),
              title: '',
              maxSize: 10000
            }];

      for (const unprocessedSlide of unprocessedSlides) {
        if (!unprocessedSlide.nodeIds.length) continue;
        if (!unprocessedSlide.nodeIds.every(nId => this.canvas.store.getNode(nId))) continue;

        let size = this.canvas.getMapViewport({
          nodes: Array.from(unprocessedSlide.nodeIds.map(nId => this.canvas.store.getNode(nId))),
          regions: [],
          drawings: [],
        });

        // If we have no unprocessedSlides we want the slide
        // size to be much larger than the default of 4000
        // and also to keep the scale at 1 (this doesn't
        // seem to play nice with the server side transition)
        this._updateSize({size, maxSize: unprocessedSlide.maxSize, autoScale: true});

        const enclosedLinkIds = this.canvas.store.links.filter(link => {
          const fromNodeIncluded = unprocessedSlide.nodeIds.includes(link.fromNodeId);
          const toNodeIncluded = unprocessedSlide.nodeIds.includes(link.toNodeId);
          const linkNodeIncluded = unprocessedSlide.nodeIds.includes(link.linkNodeId);
          return (fromNodeIncluded && toNodeIncluded) || linkNodeIncluded;
        }).map(l => l.id);

        const nodeIds = unprocessedSlide.nodeIds;

        for (const linkId of enclosedLinkIds) {
          const link = this.canvas.store.getLink(linkId);
          if (link.linkNodeId && store.getNode(link.linkNodeId)) {
            if (!nodeIds.includes(link.linkNodeId)) {
              nodeIds.push(link.linkNodeId);
            }
          }
        };

        slides.push({
          nodeIds,
          linkIds: enclosedLinkIds,
          title: unprocessedSlide.title,
          size,
          background: COLOR_VAR_TO_HEX_MAPPING[window.user.settings.background_color],
          width: size.width,
          height: size.height,
        });
      }

      const size = this.canvas.getMapViewport({
        nodes: this.canvas.store.getNodesArray(),
        regions: [],
        drawings: [],
      });

      const html = exportScreenshots._getMapHTML({
        body: exportScreenshots._prepareHtml({
          size,
        }),
        hidePrintFooter: true,
        styleSheetText: exportScreenshots._cssText(),
        subscriber: credentials.subscriber
      });

      const slidesData = LZString.compressToBase64(JSON.stringify(slides));
      const htmlData = LZString.compressToBase64(JSON.stringify(html));
      pptSlidesField.value = slidesData;
      pptHTMLField.value = htmlData;
      pptTokenField.value = credentials.token;
      pptPrintEntireMapField.value = false;
      pptForm.submit();
    };
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  _cssText() {
    let cssText = '';

    for (const sheet of Array.from(document.styleSheets)) {
      if (sheet.title && sheet.title == 'zoomStyleCSS') {
        continue;
      }
      if (sheet.href && sheet.href.match(/(googleapis|font-awesome-kingfisher)/)) {
        continue;
      }
      let rules = [];
      try {
        rules = Array.from(sheet.cssRules || sheet.rules || []);
      } catch (err) {
        console.log('css  error',err);
        continue;
      };
      for (const rule of rules) {
        if (rule.media && rule.media.mediaText && rule.media.mediaText.match(/print/)) {
          continue;
        }
        // translate back to standard property name
        const text = rule.cssText.replace('-moz-max-content', 'max-content');
        cssText += text + '\n';
      }
    }
    return cssText;
  }

  _prepareHtml({size, nodeIds}) {

    canvas.clearSelectedElements();
    canvas.linksTag.update();

    const html = document.body.innerHTML;
    const d = document.createElement('div');
    const { isTabActive } = this.canvas.toolsSidebarTag;

    d.innerHTML = html;
    // strip scripts
    Array.from(d.querySelectorAll('script')).map(e => e.parentNode.removeChild(e));
    Array.from(d.querySelectorAll('video')).map(e => e.parentNode.removeChild(e));

    if (nodeIds) {
      for (const nodeId of nodeIds) {
        const element = d.querySelector(`[data-id="${nodeId}"]`);
        element.classList.remove('selected','expanded');
        element.classList.add('selected-for-export');
      }
    } else {
      Array.from(d.querySelectorAll('.selected,.expanded')).map(e => {
        e.classList.remove('selected','expanded');
        e.classList.add('selected-for-export');
      });
    }

    const perspectives = pui.getPerspectives();
    const tagLegend = d.querySelectorAll('#perspective-sidebar-section')[0];
    const SIDEBAR_ROW_HEIGHT = 30;
    let estimatedPerspectiveListHeight = (perspectives.length * SIDEBAR_ROW_HEIGHT);
    let estimatedWaypointListHeight = 0;
    let sidebarShowing = true;
    const collabInputs = d.querySelectorAll('[data-collaborator-id]');
    if (collabInputs) {
      for (const element of collabInputs) {
        element.style.background = '';
        element.style.height = '';
        element.style.minHeight = '';
        element.style.minWidth = '';
      }
    }

    if (isTabActive('waypoints-tab')) {
      if (store.presentations[0]) {
        const waypoints = store.presentations[0].getWaypoints();
        estimatedWaypointListHeight = waypoints.length * SIDEBAR_ROW_HEIGHT;
      }
    }

    if (!isTabActive('waypoints-tab') && !isTabActive('perspectives-listing')) {
      sidebarShowing = false;
    }

    // go through all links that are contained within children of a selected card and flag them as visible
    if (this.exportSelection()) {

      const {selectedNodes,selectedLinks} = appUI.getMemberRelationshipElements(this.getExportedElements());

      const allSelectedNodes = selectedNodes.concat(this.getExportedNodes());
      const allSelectedLinks = selectedLinks.concat(this.getExportedLinks());
      const exportedNodeIds = allSelectedNodes.map(n => n.id);

      for (const link of allSelectedLinks) {
        if (link.linkNode() && !exportedNodeIds.includes(link.linkNode().id)) {
          d.querySelector(`#text-path-${link.id}`).style.clipPath = null;
          d.querySelector(`#text-path-${link.id}-backing`).style.clipPath = null;
        }
      }

      for (const link of allSelectedLinks) {
        const linkElement = d.querySelectorAll(`.link-${link.id}`)[0];
        if (linkElement) {
          if (this.orphanedLink(link,allSelectedNodes)) {
            linkElement.classList.remove('selected-for-export','selected');
          } else {
            linkElement.classList.add('selected-for-export');
          }
        }
      }
      for (const node of allSelectedNodes) {
        const nodeElement = d.querySelectorAll(`.container[data-id="${node.id}"]`)[0];
        if (nodeElement) {
          nodeElement.classList.add('selected-for-export');
        }
      }

      if (perspectives.length && isTabActive('perspectives-listing')) {
        const perspectivesInSelection = {};
        for (const node of allSelectedNodes) {
          if (node.instance.hidden) {
            continue;
          }
          for (const p of perspectives) {
            if (p.hasMemberNode(node.id) || p.isPointNode(node.id)) {
              perspectivesInSelection[p.id] = 1;
            }
          }
        }
        for (const link of allSelectedLinks) {
          for (const p of perspectives) {
            if (p.hasMemberLink(link.id)) {
              perspectivesInSelection[p.id] = 1;
            }
          }
        }

        // if there's no tags in the selection, hide the tag legend
        if (Object.keys(perspectivesInSelection).length == 0) {
          if (tagLegend) {
            tagLegend.style.display = 'none';
            if (!isTabActive('waypoints-tab')) {
              sidebarShowing = false;
            }
          }
        } else {
          // hide all tags in legend that aren't in selection
          for (const p of perspectives) {
            if (!perspectivesInSelection[p.id]) {
              const tagElement = d.querySelectorAll(`perspectives-listing-item[data-id="${p.id}"]`)[0];
              tagElement.style.display = 'none';
            }
          }
          estimatedPerspectiveListHeight = Object.keys(perspectivesInSelection).length * SIDEBAR_ROW_HEIGHT;
        }
      } else {
        estimatedPerspectiveListHeight = 0;
      }
    }
    // if the perspective list and/or waypoint list goes off the screen, adjust our map size
    const totalSidebarHeight = estimatedWaypointListHeight + estimatedPerspectiveListHeight + 50;
    if (size.height < totalSidebarHeight) {
      size.height = totalSidebarHeight;
    }
    if (!sidebarShowing && size.sidebarWidth) {
      size.width -= size.sidebarWidth;
      size.minX += size.sidebarWidth;
    }
    d.querySelector('#canvas').setAttribute('data-zoom-level',1);
    const removeClasses = [
      'top-hover',
      zoomUI.ZOOM_THRESHOLD_CLASS_0
    ];

    for (const c of removeClasses) {
      Array.from(d.querySelectorAll(`.${c}`)).map(e => {
        e.classList.remove(c);
      });
    }

    // update panzoom matrix to use 1:1 scale and position at top/left of map
    const scale = size.scale;
    d.querySelectorAll('#canvas')[0].style.transform = `matrix(${scale},0,0,${scale},${-5000 - (size.minX * scale) + 100 + size.sidebarWidth},${-5000 - (size.minY * scale) + 100})`;
    let fullhtml = d.innerHTML;
    fullhtml = fullhtml.replace(/\sns\d+\:href\=/g,' xlink:href=');
    return fullhtml;
  }


  _getMapHTML({body, styleSheetText, orientationCss, subscriber, hidePrintFooter}) {
    let showTagSelected = '';
    if (document.body.classList.contains('show-tag-selected')) {
      showTagSelected = 'show-tag-selected';
    }

    return `<body class="hide-app-chrome ${showTagSelected}">
      <link href="https://fonts.googleapis.com/css?family=Roboto:400,500,700,900" rel="stylesheet">
      <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.css">
      <style>
      ${styleSheetText}

      #banner-container,
      #home-link-dropdown,
      #intercom-frame,
      #intercom-launch-button,
      #right-sidebar,
      #zoom-tools,
      .add-perspective-button,
      .map-alert,
      .menu-tool,
      .relationship-tool .rel,
      .user-account,
      app-footer .instruction-bar,
      onboarding-popup,
      multi-selection-indicator-box,
      app-header .title-dropdown-toggle,
      app-header label,
      app-notification,
      bulk-actions,
      .bulk-actions-menu,
      i.sidebar-toggle,
      map-menu,
      create-a-node-button,
      #header-link,
      waypoints-tab,
      .menu-list,
      .side-tab--collapsed,
      .floating-menu_button,
      .side-tab_header_icon,
      .side-tab_flap,
      .side-tab_action,
      .side-tabs,
      .zen_mode_persistent,
      presence,
      presence-cursors,
      .browser-alert,
      .node-comments_button,
      comments-layer,
      comments-container,
      navigation-switcher,
      .cursor-wrapper {
        display: none !important;
      }
      /* For some reason firefox strips this style when it renders */
      .region-screen, .screen {
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }
      .app-header {
        ${hidePrintFooter ? 'display: none;' : ''}
        position: static;
        top: -1000px;
        transform: translateY(-100px);
      }
      .app-header_title {
        ${hidePrintFooter ? 'display: none;' : ''}
        font-size: 18px;
        margin: 0px;
        padding: 0px 8px;
        color: #8595a9;
        max-width: calc(100% - 215px) !important;
        position: fixed;
        top: calc(100vh + 65px);
        left: 20px;
        font-weight: 500;
        transform: translateY(-50%);
      }
      app-footer .print-footer {
        display: ${hidePrintFooter ? 'none' : 'block'};
      }
      tools-sidebar .tools {
        top: 24px !important;
        max-height: unset;
      }

      perspectives-listing .scroll-container {
        max-height: unset;
      }

      perspectives-listing .input-container,
      perspectives-listing .add-button.active {
        display: none;
      }
      perspectives-listing .context-menu {
        visibility: hidden !important;
      }
      perspectives-banner {
        display: none;
      }
      .side-tab_body {
        max-height: unset;
        background: white !important;
      }
      .region-resize-handle {
        display: none;
      }

      /* fix for export in safari - code that safari generates doesn't work well in chrome/puppeteer - clone of content in scss/icon-font.scss */
      @font-face {
        font-family: 'KingfisherIcons';
        font-style: normal;
        font-weight: 400;
        src: url('data:application/x-font-woff;base64, d09GRgABAAAAAASEAAsAAAAABDgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABPUy8yAAABCAAAAGAAAABgDxIFgGNtYXAAAAFoAAAAVAAAAFQXVtKIZ2FzcAAAAbwAAAAIAAAACAAAABBnbHlmAAABxAAAAHQAAAB0w5oCSmhlYWQAAAI4AAAANgAAADYO+cqTaGhlYQAAAnAAAAAkAAAAJAeoA8dobXR4AAAClAAAABgAAAAYDgAAZmxvY2EAAAKsAAAADgAAAA4AYgBAbWF4cAAAArwAAAAgAAAAIAAIAAVuYW1lAAAC3AAAAYYAAAGGmUoJ+3Bvc3QAAARkAAAAIAAAACAAAwAAAAMDVQGQAAUAAAKZAswAAACPApkCzAAAAesAMwEJAAAAAAAAAAAAAAAAAAAAARAAAAAAAAAAAAAAAAAAAAAAQAAA6QEDwP/AAEADwABAAAAAAQAAAAAAAAAAAAAAIAAAAAAAAwAAAAMAAAAcAAEAAwAAABwAAwABAAAAHAAEADgAAAAKAAgAAgACAAEAIOkB//3//wAAAAAAIOkA//3//wAB/+MXBAADAAEAAAAAAAAAAAAAAAEAAf//AA8AAQAAAAAAAAAAAAIAADc5AQAAAAABAAAAAAAAAAAAAgAANzkBAAAAAAEAAAAAAAAAAAACAAA3OQEAAAAAAQAz//0D5gOXAAIAABMBETMDswHKAc38ZgAAAAEAM//9A80DlwACAAAJAREDzfxmAcr+MwOaAAABAAAAAAAA7X644V8PPPUACwQAAAAAANX6wwEAAAAA1frDAQAA//0D5gOXAAAACAACAAAAAAAAAAEAAAPA/8AAAAQAAAAAAAPmAAEAAAAAAAAAAAAAAAAAAAAGBAAAAAAAAAAAAAAAAgAAAAQAADMEAAAzAAAAAAAKABQAHgAsADoAAAABAAAABgADAAEAAAAAAAIAAAAAAAAAAAAAAAAAAAAAAAAADgCuAAEAAAAAAAEABwAAAAEAAAAAAAIABwBgAAEAAAAAAAMABwA2AAEAAAAAAAQABwB1AAEAAAAAAAUACwAVAAEAAAAAAAYABwBLAAEAAAAAAAoAGgCKAAMAAQQJAAEADgAHAAMAAQQJAAIADgBnAAMAAQQJAAMADgA9AAMAAQQJAAQADgB8AAMAAQQJAAUAFgAgAAMAAQQJAAYADgBSAAMAAQQJAAoANACkaWNvbW9vbgBpAGMAbwBtAG8AbwBuVmVyc2lvbiAxLjAAVgBlAHIAcwBpAG8AbgAgADEALgAwaWNvbW9vbgBpAGMAbwBtAG8AbwBuaWNvbW9vbgBpAGMAbwBtAG8AbwBuUmVndWxhcgBSAGUAZwB1AGwAYQByaWNvbW9vbgBpAGMAbwBtAG8AbwBuRm9udCBnZW5lcmF0ZWQgYnkgSWNvTW9vbi4ARgBvAG4AdAAgAGcAZQBuAGUAcgBhAHQAZQBkACAAYgB5ACAASQBjAG8ATQBvAG8AbgAuAAAAAwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==')
        format('woff');
      }

      ${orientationCss || ''}
      ${this.selectedElementCSS()}
      </style>
      ${body}</body>`;
  }

  /* if elements are selected, hide non-selected elements, and show all selected elements and their descendants */
  selectedElementCSS() {
    if (!this.exportSelection()) {
      return '';
    }
    return `
      .link-container.selected-for-export {
        visibility: visible;
      }
      .region.selected-for-export,
      .container.selected-for-export,
      .container.selected-for-export .container,
      .container.selected-for-export .container > .screen,
      .container.selected-for-export .container > .backdrop > .col-group-container > child-nodes > .child-nodes-container > .child-nodes > .screen,
      .container.selected-for-export .container > .backdrop > .row > .dot {
        visibility: visible;
      }
      .region:not(.selected-for-export),
      .link-container:not(.selected-for-export),
      .container:not(.selected-for-export),
      .container:not(.selected-for-export) > .screen,
      .container:not(.selected-for-export) > .backdrop > .col-group-container > child-nodes > .child-nodes-container > .child-nodes > .screen  {
        visibility: hidden;
      }
      .container:not(.selected-for-export) > .backdrop > .row > .dot {
        visibility: hidden;
      }
    `;
  }

  _getLandscapePrintCss() {
    return `
      @media print{
        @page {size: landscape}
      }
    `;
  }

  // cap at a max size and resize / scale accordingly
  _updateSize({ size, maxSize, autoScale }) {

    if (autoScale) {
      // target a somewhat arbitrary large number that's
      // compatible with the pptx exporter
      const targetResolution = 3000;
      const targetScale = targetResolution / Math.max(size.width, size.height);
      const factor = targetScale / size.scale;
      size.scale *= factor;
      size.width *= factor;
      size.height *= factor;
    }

    if (!maxSize) maxSize = 4000;
    if (size.height >= maxSize || size.width >= maxSize) {
      if (size.height > size.width) {
        size.scale = maxSize / size.height;
        size.height = maxSize;
        size.width = size.scale * size.width;
      } else {
        size.scale = maxSize / size.width;
        size.width = maxSize;
        size.height = size.scale * size.height;
      }
    }
  }
  getMapSize() {
    // deselect single card so that expanded state doesn't get rendered
    if (this.canvas.getSelectedNodes().length == 1) {
      const node = this.canvas.getSelectedNodes()[0];
      if (this.canvas.nodeTags[node.id]) {
        this.canvas.nodeTags[node.id].unsetSelected();
      }
    }
    if (this.exportSelection()) {
      const {selectedNodes,selectedLinks} = appUI.getMemberRelationshipElements(this.getExportedElements());
      let allSelectedNodes = this.getExportedNodes();
      allSelectedNodes = allSelectedNodes.concat(selectedNodes);
      const selectedRegions = this.getExportedRegions();
      const selectedDrawings = this.getExportedDrawings();
      return this.canvas.getMapViewport({
        nodes: allSelectedNodes.map(n => n.instance),
        regions: selectedRegions,
        drawings: selectedDrawings,
      });
    } else {
      return this.canvas.getMapViewport({
        nodes: Array.from(this.canvas.store.getNodes()),
        regions: this.canvas.store.getRegions(),
        drawings: this.canvas.store.getDrawings(),
      });
    }
  }

  // return true if the list of nodes doesn't contain this link's from/to node or its R node
  orphanedLink(link,allSelectedNodes) {
    const fromNode = allSelectedNodes.find(n => n.id == link.fromNodeId);
    const toNode = allSelectedNodes.find(n => n.id == link.toNodeId);
    if (link.linkNodeId) {
      const linkNode = allSelectedNodes.find(n => n.id == link.linkNodeId);
      if (linkNode) {
        return false;
      }
    }

    if (!fromNode || !toNode) {
      return true;
    }

    return false;
  }

  downloadHTML() {
    this.canvas.mapMenuTag.hide();
    const size = this.getMapSize();

    // if map is greater than max size, scale it down to fit in that space
    this._updateSize({size});
    let html = this._getMapHTML({
      body: this._prepareHtml({size}),
      styleSheetText: this._cssText(),
    });
    var element = document.createElement('a');
    element.setAttribute('href', 'data:text/html;charset=utf-8,' + encodeURIComponent(html));
    element.setAttribute('download', 'map-download.html');

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }

}

const exportScreenshots = new ExportScreenshots();

export { ExportScreenshots, exportScreenshots };



// WEBPACK FOOTER //
// ./starling/src/lib/export-screenshots.js